import React, { useState } from "react";
import {
    Card, CardContent, Box, Typography, Avatar,
    IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BarChartIcon from "@mui/icons-material/BarChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

/* -------- helpers estética -------- */
const ACCENTS = [
    { bg: "rgba(25,118,210,0.08)", dot: "#1976d2" },
    { bg: "rgba(156,39,176,0.08)", dot: "#9c27b0" },
    { bg: "rgba(46,125,50,0.10)", dot: "#2e7d32" },
    { bg: "rgba(255,111,0,0.10)", dot: "#ff6f00" },
];
const prettyLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

function cardBorderColor(theme, presentes, total) {
    if (!total) return theme.palette.divider;
    const pct = (presentes / total) * 100;
    if (pct === 100) return theme.palette.success.light;
    if (pct < 70) return theme.palette.error.light;
    if (pct < 100) return theme.palette.warning.light;
    return theme.palette.divider;
}

/* -------- Metric con auto-ajuste de fuente -------- */
function Metric({ label, value, onClick, numColor, disabled, compact }) {
    const clickable = !!onClick && !disabled;
    const digits = String(value ?? "").replace(/\D/g, "").length;

    // Auto-ajuste según cantidad de dígitos o si nos piden "compact"
    let fontSize = "clamp(1.9rem, 5vw, 2.0rem)";
    if (compact || digits >= 3) fontSize = "clamp(1.5rem, 3.8vw, 1.8rem)";
    if (digits >= 4) fontSize = "clamp(1.4rem, 3.6vw, 1.6rem)";
    if (digits >= 5) fontSize = "clamp(1.2rem, 3.2vw, 1.4rem)";

    return (
        <Box
            onClick={clickable ? onClick : undefined}
            sx={{
                cursor: clickable ? "pointer" : "default",
                p: 0.2,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                background: "linear-gradient(180deg, rgba(255,255,255,0.6), rgba(255,255,255,0.35))",
                transition: "transform 120ms ease, box-shadow 120ms ease, background 120ms ease, opacity 120ms ease",
                "&:hover": clickable ? {
                    transform: "translateY(-2px)",
                    boxShadow: 2,
                    background: "rgba(255,255,255,0.85)",
                } : undefined,
                textAlign: "center",
                opacity: disabled ? 0.5 : 1,
            }}
        >
            <Typography
                sx={{
                    fontFamily: '"Orbitron", monospace',
                    fontWeight: 600,
                    fontSize,
                    lineHeight: 1,
                    color: numColor || "text.primary",
                    letterSpacing: 0.2,
                }}
            >
                {value}
            </Typography>
            <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.1, letterSpacing: 0.3, color: "text.secondary" }}
            >
                {prettyLabel(label)}
            </Typography>
        </Box>
    );
}

/* -------- BranchCard -------- */
export default function BranchCard({ suc, idx, onOpen, onMenu }) {
    const accent = ACCENTS[idx % ACCENTS.length];
    const total = Number(suc.total || 0);
    const presentes = Number(suc.presentes || 0);

    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);
    const handleMenuClick = (e) => setAnchorEl(e.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleSelect = (key) => { handleMenuClose(); onMenu?.(key, suc); };

    const isEmpresa = Boolean(suc._empresa === true);

    return (
        <Card
            elevation={0}
            sx={(theme) => ({
                height: "100%",
                borderRadius: 2,
                border: "1px solid",
                borderColor: cardBorderColor(theme, presentes, total),
                backgroundColor: "background.paper",
                transition: "box-shadow .15s ease, transform .15s ease, border-color .2s ease",
                "&:hover": { boxShadow: 3 },
            })}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 0.3,
                    pt: 0.1,
                    pb: 0.1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    background: accent.bg,
                    position: "relative",
                }}
            >
                <Avatar
                    sx={{
                        width: 50,
                        height: 50,
                        fontWeight: 800,
                        color: accent.dot,
                        background: "linear-gradient(135deg, #ffffff 0%, #f3f6ff 100%)",
                        border: "1px solid",
                        borderColor: accent.dot,
                        boxShadow: "0 6px 12px rgba(0,0,0,.08)",
                    }}
                >
                    {isEmpresa ? "EMP" : suc.sucursal_id}
                </Avatar>

                <Tooltip title={suc.sucursal_nombre}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 800,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            pr: 2,
                        }}
                    >
                        {suc.sucursal_nombre}
                    </Typography>
                </Tooltip>

                {!isEmpresa && (
                    <IconButton
                        size="small"
                        onClick={handleMenuClick}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                        aria-label="Más opciones"
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                )}

                {!isEmpresa && (
                    <Menu
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        <MenuItem onClick={() => handleSelect("mensual_fichadas")}>
                            <ListItemIcon><CalendarMonthIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Fichadas mensuales" secondary="Día por día" />
                        </MenuItem>
                        <MenuItem onClick={() => handleSelect("mensual_totales_empleado")}>
                            <ListItemIcon><SummarizeIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Totales por empleado (mes)" secondary="Horas y conteos" />
                        </MenuItem>
                        <MenuItem onClick={() => handleSelect("mensual_ausentismo")}>
                            <ListItemIcon><BarChartIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Ausentismo (mes)" secondary="Gráficos y métricas" />
                        </MenuItem>
                        <MenuItem onClick={() => handleSelect("tendencias")}>
                            <ListItemIcon><TimelineIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Tendencias" secondary="Comparar meses" />
                        </MenuItem>
                    </Menu>
                )}
            </Box>

            {/* KPIs */}
            <CardContent sx={{ p: 0.5, }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                        gap: 1,
                    }}
                >
                    <Metric
                        label="Presentes"
                        value={suc.presentes ?? 0}
                        onClick={isEmpresa ? undefined : () => onOpen("presentes")}
                        numColor="success.main"
                        disabled={isEmpresa}
                        compact={isEmpresa}
                    />
                    <Metric
                        label="Ausentes"
                        value={suc.ausentes ?? 0}
                        onClick={() => onOpen("ausentes")}
                        numColor="error.main"
                        compact={isEmpresa}
                    />
                    <Metric
                        label="Total"
                        value={suc.total ?? 0}
                        onClick={isEmpresa ? undefined : () => onOpen("total")}
                        numColor="text.primary"
                        disabled={isEmpresa}
                        compact={isEmpresa}
                    />
                </Box>
            </CardContent>
        </Card>
    );
}
